import React from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { BackgroundColors, Variants } from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { ContentType, layoutOptions } from "./types/prop-types";

/** Content Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.None,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

let options: any[];

export const Content = primaryBlock<ContentType>(
  blockConfig,
  ({
    tagline,
    header,
    description,
    items,
    categories,
    layoutProps,
  }: ContentType) => {
    const col = items.length < 3 ? "_2col" : "";
    const headerIsCenter = layoutProps.headerPlacement === "center";
    return (
      <Box className="primary-content-table-cols-v1">
        <Box className="padding-global">
          <Box className="container-large">
            <Box className="padding-section-large">
              {(header || description) && (
                <Box
                  sx={{ textAlign: headerIsCenter ? "center" : "left" }}
                  className="margin-bottom margin-xxlarge"
                >
                  <Box
                    className={`max-width-large ${
                      headerIsCenter && "align-center"
                    }`}
                  >
                    {tagline && (
                      <Box className="margin-bottom margin-xsmall">
                        <Variants.TagLine>{tagline}</Variants.TagLine>
                      </Box>
                    )}
                    {header && (
                      <Box className="margin-bottom margin-small">
                        <Variants.Heading>{header}</Variants.Heading>
                      </Box>
                    )}
                    {description && (
                      <Variants.ParagraphMedium>
                        {description}
                      </Variants.ParagraphMedium>
                    )}
                  </Box>
                </Box>
              )}
              <Box className="pricing22_component">
                <Box className={`pricing22_top-row ${col}`}>
                  <Box className="pricing22_empty-space" />

                  {/* start of pricing */}
                  {items.map((item, index) => (
                    <Box
                      key={`pricing-${index}`}
                      className="pricing22_top-row-content"
                    >
                      <Box className="pricing22_top-row-wrapper">
                        {item.tagline && (
                          <Variants.Heading className="item-tagline" as="h6">
                            {item.tagline}
                          </Variants.Heading>
                        )}
                        <Box className="margin-vertical margin-xsmall">
                          <Variants.PriceText
                            sx={{
                              display: "inline",
                              fontSize:
                                item.price.length > 5 ? "2rem" : "3.5rem",
                            }}
                          >
                            {item.price}
                          </Variants.PriceText>
                          <Variants.PeriodText className="period">
                            {item.period}
                          </Variants.PeriodText>
                        </Box>
                        <Variants.ParagraphDefault>
                          {item.description}
                        </Variants.ParagraphDefault>
                      </Box>
                      {item.topButtonText && (
                        <Box className="pricing22_top-row-button">
                          <Box className="margin-top margin-medium">
                            <Variants.LinkButton
                              href={item.topButtonLink}
                              className="button max-width-full is-pricing22-button w-button"
                            >
                              {item.topButtonText}
                            </Variants.LinkButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                  {/* end of pricing */}
                </Box>

                {/* start of table */}
                {categories.map((category, index) => (
                  <Box key={`category-${index}`}>
                    <Box className="pricing22_heading-row">
                      <Variants.Heading as="h6">
                        {category.title}
                      </Variants.Heading>
                    </Box>
                    {category.rows.map((row, rowIndex) => {
                      options =
                        row.options.length > items.length
                          ? row.options.slice(0, items.length)
                          : row.options;

                      const isBackgroundColorSameAsOdd =
                        layoutProps.backgroundColor ===
                          BackgroundColors.neutral ||
                        layoutProps.backgroundColor === BackgroundColors.dark;
                      const isOdd = (rowIndex ?? 0) % 2 === 1;
                      return (
                        <Variants.GridOddEven
                          odd={isBackgroundColorSameAsOdd ? !isOdd : isOdd}
                          key={`row-${rowIndex}`}
                          className={`pricing22_row ${col}`}
                        >
                          <Box
                            id="category"
                            className={`pricing22_feature ${col}`}
                          >
                            <Variants.ParagraphDefault>
                              {row.title}
                            </Variants.ParagraphDefault>
                          </Box>
                          {options.map((option, optionIndex) => (
                            <Box
                              key={`option-${optionIndex}`}
                              className={`pricing22_row-content ${col}`}
                            >
                              {option.icon ? (
                                <Variants.IconMedium
                                  icon={option.icon}
                                  hasBg={false}
                                  noPrimary
                                  className="icon-1x1-xsmall"
                                />
                              ) : (
                                option.text
                              )}
                            </Box>
                          ))}
                        </Variants.GridOddEven>
                      );
                    })}
                  </Box>
                ))}
                {/* end of table */}

                <Box className={`pricing22_bottom-row-2 ${col}`}>
                  <Box className="pricing22_empty-space" />

                  {/* start bottom buttons */}
                  {items.map(
                    (item, index) =>
                      item.bottomButtonText && (
                        <Box
                          key={`button-${index}`}
                          className="pricing22_bottom-row-content"
                        >
                          <Variants.LinkButton
                            href={item.bottomButtonLink}
                            className="button max-width-full is-pricing22-button w-button"
                          >
                            {item.bottomButtonText}
                          </Variants.LinkButton>
                        </Box>
                      )
                  )}
                  {/* end bottom buttons */}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

Content.defaultProps = getDefaultProps<ContentType>(defaults);

export const Element: KameleonElement = {
  component: Content,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
